import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE } from '../../constants';
import styled from 'foundations-theming/utils';
import { FieldsetContextProvider } from '../../context/FieldsetContext';
import { HelpIcon } from '../HelpIcon/HelpIcon';
import { ScreenReaderOnly } from '../ScreenReaderOnly/ScreenReaderOnly';
import { Overlay } from '../Overlay/Overlay';
import * as CustomRenderer from '../AbstractPageTemplate/customRenderer';
import { Flex } from '../../../components/Flex/Flex';
import { Text } from '../../../components/Text/Text';
import { View } from '../../../components/View/View';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { deepMerge } from '../../utils/deepMerge';
import { useInputGroupContext } from '../InputGroup/InputGroupProvider';
import { MIN_WIDTH } from '../../../constants/nonTokenisedValues';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const Outer = styled.fieldset.withConfig({
  displayName: "Fieldset__Outer"
})(({
  theme,
  $cssStyles
}) => {
  return Object.assign({
    position: 'relative',
    minWidth: MIN_WIDTH.FIELDSET_OUTER_WRAPPER,
    padding: theme.space[0],
    margin: theme.space[0],
    border: theme.borderWidth[0]
  }, $cssStyles);
});
export const StyledSpan = styled.span.withConfig({
  displayName: "Fieldset__StyledSpan"
})(({
  $styleOverrides
}) => {
  return deepMerge({
    fontWeight: 'inherit'
  }, $styleOverrides);
});
export const FieldsetLegend = ({
  children,
  id,
  disabled,
  required
}) => {
  var _inputGroupContext$fi;
  const inputGroupContext = useInputGroupContext();
  return /*#__PURE__*/_jsxs(View, {
    cursor: disabled ? 'not-allowed' : 'default',
    paddingInlineEnd: "0",
    paddingBlockEnd: "0",
    as: "span",
    children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
      children: /*#__PURE__*/_jsx("span", {
        id: id,
        children: children
      })
    }), /*#__PURE__*/_jsxs(StyledSpan, {
      "aria-hidden": "true",
      $styleOverrides: (_inputGroupContext$fi = inputGroupContext === null || inputGroupContext === void 0 ? void 0 : inputGroupContext.fieldsetStylesOverrides) !== null && _inputGroupContext$fi !== void 0 ? _inputGroupContext$fi : {},
      children: [children, required && /*#__PURE__*/_jsx("span", {
        children: " *"
      })]
    })]
  });
};
export const Fieldset = _ref => {
  let {
      children,
      disabled = false,
      Legend = FieldsetLegend,
      legend,
      legendId,
      overlay,
      _size = 'default',
      _required = false,
      help,
      tooltip,
      color,
      tooltipPlacement = 'top',
      [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, ["children", "disabled", "Legend", "legend", "legendId", "overlay", "_size", "_required", "help", "tooltip", "color", "tooltipPlacement", DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE].map(_toPropertyKey));
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const hasOverlay = !!overlay;
  const computedDisabled = hasOverlay ? true : disabled;
  const renderedLegend = legend ? /*#__PURE__*/_jsx(Legend, {
    id: legendId,
    required: _required,
    disabled: disabled,
    children: legend
  }) : null;
  const renderedLegendWithTooltip = tooltip ? /*#__PURE__*/_jsx(HelpIcon, {
    title: tooltip,
    tooltipPlacement: tooltipPlacement,
    color: color,
    children: renderedLegend
  }) : renderedLegend;
  return /*#__PURE__*/_jsxs(Outer, Object.assign({
    "aria-hidden": hasOverlay,
    disabled: computedDisabled,
    $cssStyles: cssStyles
  }, domProps, {
    [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute || 'Fieldset',
    children: [/*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        paddingBlockStart: renderedLegendWithTooltip != null ? '200' : '0',
        paddingBlockEnd: help != null || renderedLegendWithTooltip != null ? '100' : '0',
        children: [renderedLegendWithTooltip, help != null ? /*#__PURE__*/_jsx(Flex, {
          children: /*#__PURE__*/_jsx(Text, {
            typography: "heading-25",
            color: "text-core-subtle",
            children: help
          })
        }) : null]
      }), /*#__PURE__*/_jsx(FieldsetContextProvider, {
        disabled: computedDisabled,
        size: _size,
        children: /*#__PURE__*/_jsx("div", {
          children: children
        })
      })]
    }), overlay && CustomRenderer.render(overlay, {
      contextual: true,
      use: overlay.type === Overlay ? 'light' : undefined
    }, {
      mergeType: 'aggressive'
    })]
  }));
};
Fieldset.displayName = 'Fieldset';